/* @generated */
import * as React from "react";
import { useAssignThemeOverrideInlineVars, useTheme } from "./hooks.esm.js";
import { ThemeContext } from "./context.esm.js";
import { getThemeClassname, getThemeInfo, defaultTheme, defaultMode, resolveThemeOverrides } from "./utils.esm.js";
import classnames from "classnames";
import { themeContainer } from "./theme-container.css.esm.js";
const ThemeProvider = ({
  mode: modeProp,
  theme: themeProp,
  _dangerouslyIncludeAdditionalClassName: additionalClassProp,
  overrides: overrideThemeProp,
  _dangerouslyEnableMultipleThemes = false,
  children
}) => {
  const {
    overrides: existingThemeOverrides,
    theme: existingTheme,
    mode: existingMode,
    additionalClass: existingAdditionalClass
  } = React.useContext(ThemeContext);
  const dangerouslySetMixedTheme = _dangerouslyEnableMultipleThemes === true && themeProp !== void 0 ? themeProp : null;
  const theme = dangerouslySetMixedTheme !== null ? (
    // If users pass in _dangerouslyEnableMultipleThemes with a custom theme, allow them to break out of the theme
    dangerouslySetMixedTheme
  ) : (
    // We don't want to allow theme mixing, so we take the theme from context first
    existingTheme || themeProp || defaultTheme
  );
  const mode = modeProp || existingMode || defaultMode;
  const additionalClass = classnames(additionalClassProp, existingAdditionalClass);
  const themeOverrides = resolveThemeOverrides({
    currentOverride: overrideThemeProp,
    existingOverride: existingThemeOverrides
  });
  const styles = useAssignThemeOverrideInlineVars({ themeOverrides, mode });
  const inverseStyles = useAssignThemeOverrideInlineVars({ themeOverrides, mode, isInverse: true });
  const className = getThemeClassname({ mode, theme, additionalClass });
  const inverseClassName = getThemeClassname({ mode, theme, isInverse: true, additionalClass });
  const themeProps = React.useMemo(
    () => getThemeInfo({ theme, mode, className, inverseClassName, styles, inverseStyles }),
    [theme, mode, className, inverseClassName, styles, inverseStyles]
  );
  return /* @__PURE__ */ React.createElement(
    ThemeContext.Provider,
    {
      value: {
        mode,
        additionalClass,
        // Be sure not to overwrite a null theme to the default in the context
        // This is important for the `useTheme` hook to work correctly
        theme: dangerouslySetMixedTheme || existingTheme || themeProp || null,
        overrides: themeOverrides
      }
    },
    typeof children === "function" ? children(themeProps) : children
  );
};
ThemeProvider.displayName = "ThemeProvider";
function removeClassesFromElement(classes, element) {
  if (element && classes.length > 0) {
    classes.forEach((c) => {
      if (element.classList.contains(c)) {
        element.classList.remove(c);
      }
    });
  }
}
const ThemeContainer = ({ children, rootElement }) => {
  const { getThemeProps } = useTheme();
  const addedClasses = React.useRef([]);
  const classList = getThemeProps().className;
  const styles = getThemeProps().style;
  React.useEffect(() => {
    if (!rootElement) {
      return;
    }
    removeClassesFromElement(addedClasses.current, rootElement);
    const classNames = classList.split(" ");
    classNames.forEach((c) => {
      if (!rootElement.classList.contains(c)) {
        rootElement.classList.add(c);
      }
    });
    addedClasses.current = classNames;
    if (styles) {
      Object.entries(styles).forEach(([key, value]) => {
        if (rootElement) {
          rootElement.style.setProperty(key, value);
        }
      });
    }
    return () => {
      removeClassesFromElement(addedClasses.current, rootElement);
      if (styles) {
        Object.keys(styles).forEach((key) => {
          rootElement.style.removeProperty(key);
        });
      }
    };
  }, [rootElement, classList, styles]);
  return /* @__PURE__ */ React.createElement("span", { ...getThemeProps({ className: themeContainer, style: { display: "contents" } }) }, children);
};
ThemeContainer.displayName = "ThemeContainer";
export {
  ThemeContainer,
  ThemeProvider
};
